import { ORPCError } from "@orpc/client";
import { StandardRPCJsonSerializer } from "@orpc/client/standard";
// One error type for every refusal the API answers, whatever shape its body has (V6 plan 13.3.1
// "Errors"): the one body every path answers since H.16, `{code, status, message, data,
// traceId}`, and the shapes a server from before it answered, so the client reads either side:
// - the RPC encoding (the alias H.19 removed, and the procedures' own paths before H.13):
//   `{json: {defined, code, status, message, data}, meta}`, or that object bare;
// - a hand-written route, or a refusal before the procedure runs (a key's 401 api_key_invalid,
//   403 api_key_wrong_site, a bearer's 401 session_invalid): `{code, params, traceId}`;
// - anything else (a proxy's 502 page, an empty body, HTML where JSON was due): `HTTP_<status>`.
// Nothing answered at all is status 0, code NETWORK_ERROR. A redirect is never followed, since it
// could carry the credential to another origin: it is `redirect_refused` at its status (0 where a
// browser hides it) with the Location, when known, in `data`. A cancelled call is never wrapped:
// it stays the AbortError its signal raised.
/** Nothing answered: the fetch itself failed (DNS, refused, reset, CORS). Its status is 0. */
export const NETWORK_ERROR = "NETWORK_ERROR";
/** The server answered a redirect, which the client never follows. */
export const REDIRECT_REFUSED = "redirect_refused";
const BRAND = Symbol.for("@mfa/v5-client.ApiClientError");
/** A refusal from /api/v5, or a call that reached nothing: `{code, status, message, data, traceId}`. */
export class ApiClientError extends Error {
    /** The stable identifier: `run_not_found`, `api_key_invalid`, `BAD_REQUEST`, `HTTP_502`, … */
    code;
    status;
    /** What the refusal carries: the RPC error's `data`, or a hand-written route's `params`. */
    data;
    /** The request id the server logged it under (`x-request-id`), when known. */
    traceId;
    [BRAND] = true;
    constructor(init, options) {
        super(init.message || init.code, options);
        this.name = "ApiClientError";
        this.code = init.code;
        this.status = init.status;
        this.data = init.data;
        this.traceId = init.traceId;
    }
    toJSON() {
        return {
            code: this.code,
            status: this.status,
            message: this.message,
            data: this.data,
            ...(this.traceId ? { traceId: this.traceId } : {}),
        };
    }
}
/**
 * Whether a value is an ApiClientError. Checked by brand rather than `instanceof`, so two copies
 * of this package (a bundler's and a workspace's) still agree.
 */
export function isApiClientError(value) {
    return (typeof value === "object" && value !== null && value[BRAND] === true);
}
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
const text = (value) => typeof value === "string" && value.length > 0 ? value : undefined;
const rpcJson = new StandardRPCJsonSerializer();
/** The RPC encoding's `{json, meta}` read back as the value it carries, `meta` applied. */
function unwrapRpc(body) {
    if (!Array.isArray(body.meta) || body.meta.length === 0)
        return body.json;
    try {
        return rpcJson.deserialize(body.json, body.meta);
    }
    catch {
        return body.json;
    }
}
/**
 * The error a response body names, at the response's status. `headers` supplies the trace id
 * (`x-request-id`) when the body carries none, as the RPC encoding never does.
 */
export function errorFromBody(status, body, headers) {
    const headerTrace = text(headers?.get("x-request-id"));
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        parsed = undefined;
    }
    const outer = isRecord(parsed) ? parsed : {};
    const unwrapped = "json" in outer && !("code" in outer) ? unwrapRpc(outer) : outer;
    const inner = isRecord(unwrapped) ? unwrapped : {};
    const code = text(inner.code);
    if (code === undefined)
        return new ApiClientError({
            code: `HTTP_${status}`,
            status,
            message: `HTTP ${status}`,
            traceId: text(outer.traceId) ?? headerTrace,
        });
    return new ApiClientError({
        code,
        status,
        message: text(inner.message) ?? code,
        data: "data" in inner ? inner.data : inner.params,
        traceId: text(inner.traceId) ?? text(outer.traceId) ?? headerTrace,
    });
}
/** The refusal a non-2xx response carries, read from its body (which this consumes). */
export async function readError(response) {
    const body = await response.text().catch(() => "");
    return errorFromBody(response.status, body, response.headers);
}
/** Whether an error is a cancellation: the AbortError (or TimeoutError) a signal raised. */
export function isAbortError(error) {
    const name = error?.name;
    return name === "AbortError" || name === "TimeoutError";
}
/** A fetch that failed before any answer, as the one error type (status 0). */
export function networkError(url, error) {
    const cause = error instanceof Error ? (error.cause ?? error) : error;
    const why = cause instanceof Error ? cause.message : String(cause);
    let where = url;
    try {
        where = new URL(url).origin;
    }
    catch {
        // A relative URL (a page's own origin): name it as given.
    }
    return new ApiClientError({ code: NETWORK_ERROR, status: 0, message: `cannot reach ${where}: ${why}` }, { cause: error });
}
/** The statuses fetch would follow; 304 and the other 3xx answers are no redirect. */
const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);
/**
 * Whether an answer is a redirect: a 301, 302, 303, 307 or 308 (Node's fetch with
 * `redirect: "manual"`), or the opaque redirect a browser answers in its place, at status 0.
 */
export function isRedirect(response) {
    return response.type === "opaqueredirect" || REDIRECT_STATUSES.has(response.status);
}
/**
 * A redirect refused, as the one error type: `redirect_refused` at the redirect's status, with
 * `data: {location}`, the target resolved against the URL asked (null where a browser hides it).
 * The only redirect V5 answers is a `www.` host's to its bare host: use that origin as baseUrl.
 */
export function redirectRefused(url, response) {
    const header = response.headers.get("location");
    let location = header;
    if (header)
        try {
            location = new URL(header, url).href;
        }
        catch {
            // Not a URL against this one: kept as sent.
        }
    return new ApiClientError({
        code: REDIRECT_REFUSED,
        status: response.status,
        message: `${url} answered a redirect${location ? ` to ${location}` : ""}, which the client never follows, so no credential leaves for another origin: check baseUrl`,
        data: { location },
        traceId: text(response.headers.get("x-request-id")),
    });
}
/**
 * What a procedure call threw, as the one error type: an ApiClientError or a cancellation stays
 * as it is, an ORPCError (a body the RPC decoder read itself) becomes an ApiClientError, and
 * anything else is passed through.
 */
export function toApiClientError(error) {
    if (isApiClientError(error) || isAbortError(error))
        return error;
    if (error instanceof ORPCError)
        return new ApiClientError({ code: error.code, status: error.status, message: error.message, data: error.data }, { cause: error });
    return error;
}
//# sourceMappingURL=errors.js.map